import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { EmptyArray } from 'lib/array/arrayUtils'
import React, { useMemo } from 'react'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { getFlightDealIdsByYearMonth, markCheapestPrice } from 'selectors/offerPage/offerPageSelectors'
import { pluralizeToString } from 'lib/string/pluralize'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireIcon'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import SolidCircleIcon from 'components/Luxkit/Icons/solid/SolidCircleIcon'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { EmptyObject } from 'lib/object/objectUtils'
import LineFireIcon from 'components/Luxkit/Icons/line/LineFireIcon'
import { INCLUSIONS_TITLE_BONUS } from 'constants/inclusions'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'

interface Props {
  fromPrice: number;
  duration: number;
  withFlights?: boolean;
  rooms: Array<App.RoomOccupants>;
  year?: string;
  month?: string;
  flightDealIdsByYearMonth?: Record<string, Record<string, string>>;
  displayMemberPrice: boolean;
  cheapest?: boolean;
  markCheapestPrice: boolean;
  hasLoyaltyRoomUpgrade?: boolean;
  hasTactical?: boolean;
}

function OfferCalendarPriceBlurb(props: Props) {
  const {
    fromPrice,
    duration,
    withFlights,
    flightDealIdsByYearMonth = EmptyObject,
    year,
    month,
    rooms = EmptyArray,
    displayMemberPrice,
    cheapest,
    markCheapestPrice,
    hasLoyaltyRoomUpgrade,
    hasTactical,
  }: Props = props

  const averagePrice = Math.ceil(fromPrice / (rooms.length > 1 ? rooms.length : duration))

  const flightDealName = useMemo(() => {
    if (year && year in flightDealIdsByYearMonth) {
      const yearRecord = flightDealIdsByYearMonth[year]

      if (month) {
        if (month in yearRecord) {
          return yearRecord[month]
        }

        return undefined
      }

      return yearRecord[Object.keys(yearRecord)[0]]
    }

    return undefined
  }, [flightDealIdsByYearMonth, year, month])

  const unit = rooms.length > 1 ? 'room' : 'night'
  const roomCount = rooms.length
  const priceColour = markCheapestPrice && cheapest ? 'highlight-secondary' : 'neutral-three'

  const [tier] = useCurrentLoyaltyTier()
  const canLoyaltyUpgrade = useUserHasLoyaltyBenefit('hotel_upgrade')

  let FireIcon
  if (isLPCTacticalEnabled()) {
    FireIcon = <CaptionTextBlock startIcon={<SolidFireIcon colour="urgency" />} variant="medium">
      Limited Time Special
    </CaptionTextBlock>
  } else {
    FireIcon = <CaptionTextBlock startIcon={<LineFireIcon colour="secondary-normal" />} variant="medium" format="titlecase">
      {INCLUSIONS_TITLE_BONUS} Available
    </CaptionTextBlock>
  }

  return (
    <Group
      direction="vertical"
      gap={4}
      tabletDirection="horizontal"
      tabletHorizontalAlign="space-between"
      fullWidth
    >
      {!withFlights && <BodyText variant="small" colour="neutral-one" >
        {displayMemberPrice && <BodyText weight="bold" colour="lux-plus" variant="small" as="span">
          Showing {LUXURY_PLUS.PROGRAM_NAME} member prices{' '}
        </BodyText>}
        {roomCount > 1 && `${pluralizeToString('room', roomCount)}, `}
        {pluralizeToString('night', duration)} from{' '}
        <BodyText variant="small" weight="bold" colour={priceColour} as="span"><FormatCurrency value={fromPrice} /></BodyText> total{' '}<CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="neutral-three" as="span">
          Average <FormatCurrency value={averagePrice} />/{unit}
        </BodyText>
      </BodyText>}
      {withFlights && <BodyText variant="small" colour="neutral-one">
        {displayMemberPrice && <BodyText weight="bold" colour="lux-plus" variant="small" as="span">
          Showing {LUXURY_PLUS.PROGRAM_NAME} member prices{' '}
        </BodyText>}
        {roomCount > 1 && `${pluralizeToString('room', roomCount)}, `}
        {pluralizeToString('night', duration)} + round trip flights from{' '}
        <BodyText variant="small" weight="bold" colour={priceColour} as="span"><FormatCurrency value={fromPrice} />*</BodyText> (2 adults){' '}<CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="neutral-three" as="span">
          Average <FormatCurrency value={averagePrice} />/{unit}
        </BodyText>
      </BodyText>}
      <Group direction="horizontal" gap={24}>
        {flightDealName && <CaptionTextBlock
          variant="medium"
          colour="neutral-two"
          startIcon={<SolidFireIcon colour="urgency" />}
        >
          {flightDealName}
        </CaptionTextBlock>}
        {hasTactical && FireIcon}
        {hasLoyaltyRoomUpgrade && canLoyaltyUpgrade && <CaptionTextBlock
          variant="medium"
          colour="neutral-two"
          startIcon={<SolidCircleIcon colour={`tier-${tier}`} />}
        >
          Free room upgrade available
        </CaptionTextBlock>}
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    duration: state.duration,
    withFlights: state.flights,
    rooms: state.rooms,
    flightDealIdsByYearMonth: getFlightDealIdsByYearMonth(appState, state).dealIds,
    displayMemberPrice: state.displayMemberPrice,
    markCheapestPrice: markCheapestPrice(state),
  }
})(OfferCalendarPriceBlurb)
